/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo } from 'react';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { MediaPowderService } from '../../../../../service/MediaPowderServices';
import LotUsageMetadataCards from "./lotUsageMetadataCards";
import LotUsageInventoryCards from "./lotUsageInventoryCards";
import LotUsageFilters from "../partials/lotUsageFilters";
import MediaPrepTransactionTable from '../../../../common/commonComponents/lotUsageTransactionTable';

interface BatchLotUsageDetailsProps {
  lotId: string;
  onBack: () => void;
  onViewReference?: (refId: string) => void;
}

const BatchLotUsageDetails: React.FC<BatchLotUsageDetailsProps> = ({ lotId, onBack, onViewReference }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const { t } = useTranslation();

  const [lotUsageDetails, setLotUsageDetails] = useState<any>(null);
  const [transactionLogList, setTransactionLogList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [batchInfo, setBatchInfo] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(0);
  
  // Fetches lot usage & transaction details (Batch Lot Distribution screen)
  useEffect(() => {
    const fetchLotUsageDetails = async () => {
      setIsLoading(true);
      try {
        const res = await MediaPowderService.getMediaPowderLotUsageDetails(
          lotId,
          currentPage,
          itemsPerPage,
          searchTerm,
          startDate,
          endDate
        );

        if (res?.status) {
          setLotUsageDetails(res.data.lot);
          setTransactionLogList(res.data.transaction_log || []);
          setTotalPages(res.totalPages || 1); // SAME AS INVENTORY
        }

        const batchId = res.data?.lot?.batch?.id;
        if (batchId) {
          const batchRes = await MediaPowderService.getMediaPowderLotDetails(batchId);
          if (batchRes?.status) {
            setBatchInfo(batchRes.batch_info);
          }
        }

        const batchData = res.data?.lot?.batch_name;
        if (batchData) {
          setBatchInfo(batchData);
        }

      } catch (err) {
        console.error("Error fetching lot usage", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (lotId) fetchLotUsageDetails();

  }, [lotId, currentPage, searchTerm, startDate, endDate]);

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-20">
      <MediaPrepHeaderSection />
      <MediaPrepPageHeader pageHeader={t('mediaPowder.lotDetails.lotUsage')} />
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all shadow-sm">
            <span className="material-symbols-outlined text-xl">{t('mediaPowder.icons.arrow_back')}</span>
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('mediaPowder.icons.inventory')}</span>
              <span className="material-symbols-outlined text-[10px] text-slate-300">{t('mediaPowder.icons.arrow_back')}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.powderBatch')}</span>
              <span className="material-symbols-outlined text-[10px] text-slate-300">{t('mediaPowder.icons.chevron_right')}</span>
              <span className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">{t('mediaPowder.lotDetails.lotDetails')}</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase flex items-center gap-3">
                {t('mediaPowder.lotDetails.lotUsage')}
              </h1>
              <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase rounded-full border border-emerald-200">
                {lotUsageDetails?.batch?.gpt_test_status || "-"}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-0.5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {t('mediaPowder.lotDetails.batch')}: <span className="text-slate-600">{lotUsageDetails?.batch?.batch_code || "-"}</span>
              </p>
              <div className="w-1 h-1 rounded-full bg-slate-300"></div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {t('mediaPowder.lotDetails.lot')}: <span className="text-slate-600">{lotUsageDetails?.lot_code || "-"}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
            <span className="material-symbols-outlined text-sm">{t('mediaPowder.icons.download')}</span>
            {t('mediaPowder.lotDetails.exportLog')}
          </button>
          <button className="px-4 py-2 bg-[#2D3E50] text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:brightness-110 shadow-sm transition-all">
            {t('mediaPowder.lotDetails.printLotLabel')}
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <LotUsageMetadataCards
          lotUsageDetails={lotUsageDetails}
          batchInfo={batchInfo}
          t={t}
        />
        <LotUsageInventoryCards
          lotUsageDetails={lotUsageDetails}
          t={t}
        />
        {/* Transaction & Usage Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/30 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-orange-500 font-black">{t('mediaPowder.icons.history')}</span>
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">{t('mediaPowder.lotDetails.transactionUsageLog')}</h3>
            </div>

          <div className="flex flex-wrap items-center gap-3">  
            <LotUsageFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              clearFilters={clearFilters}
              t={t}
            />
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg font-black text-[9px] uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
                <span className="material-symbols-outlined text-sm">{t('mediaPowder.icons.file_download')}</span>
                {t('mediaPowder.lotDetails.exportTable')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <MediaPrepTransactionTable
              paginatedTransactions={transactionLogList}
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              filteredTransactions={transactionLogList}
              itemsPerPage={itemsPerPage}
              onViewReference={onViewReference}
              t={t}
            />
        </div>
      </div>
    </div>
   </div>
    );
};

export default BatchLotUsageDetails;
